import React, { useMemo } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Mirrors services/content_sanitizer.py's tag/attribute allowlist. Kept as a
// separate constant (not fetched from the config endpoint) because DOMPurify
// needs it synchronously on first render and this is a second, independent
// check -- the server has already sanitised on write, so this is defence in
// depth against a stored value from before a tightened allowlist, not the
// primary control.
const ALLOWED_TAGS = [
  'p', 'br', 'blockquote', 'code', 'pre',
  'strong', 'b', 'em', 'i', 'u', 'span',
  'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a',
  'sup',
];
const ALLOWED_ATTR = ['href', 'title', 'style', 'target', 'rel'];

/**
 * Renders Document.content (Markdown, possibly with a small allowlisted HTML
 * subset for underline/sized text) for the view page.
 *
 * This is the first `dangerouslySetInnerHTML` in the codebase. It is safe
 * because the value is double-sanitised: the server strips anything outside
 * CONTENT_FEATURES on every write (services/content_sanitizer.py), and
 * DOMPurify strips anything outside the mirrored allowlist here again before
 * it ever reaches the DOM -- so even a row written before a stricter
 * allowlist shipped, or a straight DB edit, cannot inject markup.
 */
function RichContentView({ content }) {
  const html = useMemo(() => {
    const rendered = marked.parse(content || '', { breaks: true });
    return DOMPurify.sanitize(rendered, { ALLOWED_TAGS, ALLOWED_ATTR });
  }, [content]);

  return (
    <div
      className="rich-content-view"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export default RichContentView;
